import React from 'react';
import { MdExpandMore, MdExpandLess, MdInfoOutline, MdDelete } from 'react-icons/md';

// Import des hooks et constantes de Thème
import { useTheme } from "../contexts/ThemeContext";
import { THEMES, THEME_COLORS } from "./Constants";

const formatCurrency = (amount) => {
    return Number(amount).toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

// Les styles de base restent ici (neutres ou sans couleur)
const styles = {
    row: {
        cursor: "pointer",
        transition: 'background-color 0.2s',
    },
    center: {
        textAlign: "center",
        padding: "3px",
    },
    left: {
        textAlign: "left",
        padding: "3px",
    },
    actionCell: {
        textAlign: "center",
        padding: "3px",
        display: 'flex',
        gap: '5px',
        justifyContent: 'center',
    },
    iconButton: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '5px',
    },
    detailTable: {
        width: 'calc(100% - 20px)',
        margin: '0px 10px 5px 10px',
        borderCollapse: 'collapse',
    },
    detailTh: {
        textAlign: 'center',
        fontSize: '0.9rem',
        padding: '3px',
    },
    detailTd: {
        textAlign: 'center',
        fontSize: '0.9rem',
        padding: '0px',
    },
};

export default function TransactionRow({
    facture,
    categories,
    allPersons,
    handleEditFacture,
    handleDeleteFacture,
    checkbox,
    isExpanded,
    onToggleExpand,
    handleSelectFacture,
    // Prop pour recevoir les styles du thème
    propThemeColors // Nom de la prop ajusté
}) {
    // 1. Appel du Hook au sommet (TOUJOURS)
    const { currentTheme } = useTheme();

    // 2. Détermination du thème de secours/par défaut
    const defaultThemeColors = THEME_COLORS[currentTheme] || THEME_COLORS[THEMES.BLUE];

    // 3. Choix final : Utilise la prop si elle est fournie, sinon utilise le contexte par défaut.
    const themeColors = propThemeColors || defaultThemeColors;


    const categoryName = categories.find((c) => c.id === facture.categorie_id)?.name || "N/A";

    const getPersonName = (id) => {
        const person = allPersons.find(p => String(p.id) === String(id));
        return person ? person.name : `Tiers ${id}`;
    };

    const paymentsToDisplay = facture.bill_payments || facture.paiements;
    const totalPaid = paymentsToDisplay ? paymentsToDisplay.reduce((sum, p) => sum + Number(p.montant || p.amount_paid || 0), 0) : 0;

    // Couleurs d'état dynamiques
    const statusColor = facture.statut === "Payé" ? themeColors.success : themeColors.danger;

    // Couleurs de fond/bordure
    const detailBg = themeColors.formBackground; // Utilisation d'un fond clair pour le détail
    const borderCol = themeColors.inputBorder;
    const detailHeaderBg = themeColors.background;


    const handleToggleDetails = (e) => {
        e.stopPropagation();
        onToggleExpand(facture.id);
    };

    const handleEditClick = (e) => {
        e.stopPropagation();
        handleEditFacture(facture);
    };

    const handleSelectColumnClick = (e) => {
        e.stopPropagation();
        handleSelectFacture(facture.id);
    };

    const COL_SPAN = 8;

    // Fonction utilitaire pour appliquer les styles de bordure de cellule
    const getCellStyle = (baseStyle) => ({
        ...baseStyle,
        borderBottom: `1px solid ${borderCol}`,
        color: themeColors.text // Assure que le texte de la cellule est en couleur texte
    });

    const getDetailCellStyle = (baseStyle) => ({
        ...baseStyle,
        borderBottom: `1px solid ${borderCol}`,
        color: themeColors.text
    });

    const getDetailHeaderStyle = (baseStyle) => ({
        ...baseStyle,
        backgroundColor: detailHeaderBg,
        borderBottom: `1px solid ${borderCol}`
    });


    return (
        <>
            {/* LIGNE PRINCIPALE DE LA FACTURE */}
            <tr
                key={facture.id}
                style={styles.row}
                onClick={handleEditClick}
            >
                {/* 1. CASE À COCHER (INCHANGÉE) */}
                <td
                    style={getCellStyle(styles.center)}
                    onClick={handleSelectColumnClick}
                    title="Cliquer pour sélectionner"
                >
                    <input
                        type="checkbox"
                        checked={checkbox.props.checked}
                        readOnly={true}
                        style={{ cursor: 'pointer' }}
                    />
                </td>

                {/* 2. DÉROULER PAIEMENTS (CLIC SUR L'ICÔNE D'INFORMATION) */}
                <td style={{ ...getCellStyle(styles.center), width: '50px' }}>
                    <button 
                        style={{ ...styles.iconButton, color: themeColors.primary }}
                        title={isExpanded ? "Replier les paiements" : "Déplier les paiements"} 
                        onClick={handleToggleDetails} // ⬅️ Clic sur l'icône déroule
                    >
                        {isExpanded ? <MdExpandLess size={18} /> : <MdExpandMore size={18} />}
                    </button>
                </td>
                
                {/* Reste des cellules */}
                <td style={getCellStyle(styles.center)}>{new Date(facture.date).toLocaleDateString("fr-FR", { timeZone: "UTC" })}</td>
                <td style={getCellStyle(styles.left)}>{categoryName} - {facture.description}</td>
                <td style={getCellStyle(styles.center)}>{formatCurrency(facture.montant)} €</td>
                <td style={{ ...getCellStyle(styles.center), color: statusColor }}>{facture.statut}</td>
                <td style={getCellStyle(styles.actionCell)}>
                    
                    <button
                        onClick={handleToggleDetails}
                        style={{ ...styles.iconButton, color: themeColors.accent }}
                        title="Voir les détails (paiements/bénéficiaires)"
                    >
                        <MdInfoOutline size={18} /> 
                    </button>
                    
                    <button
                        onClick={(e) => { e.stopPropagation(); if (window.confirm(`Voulez-vous vraiment supprimer la facture de ${formatCurrency(facture.montant)} € ?`)) { handleDeleteFacture(facture.id); } }}
                        style={{ ...styles.iconButton, color: themeColors.danger }}
                        title="Supprimer la facture"
                    >
                        <MdDelete size={18} />
                    </button>
                </td>
            </tr>

            {/* LIGNE DES DÉTAILS DE PAIEMENT (Affichée si isExpanded est true) */}
            {isExpanded && paymentsToDisplay && (
                <tr style={{ ...styles.row, backgroundColor: detailBg }}>
                    <td colSpan={COL_SPAN} style={{ padding: '0' }}>
                        <h4 style={{ margin: '5px 0 5px 20px', fontSize: '0.9rem', color: themeColors.text }}>Détails des règlements :</h4>
                        {paymentsToDisplay.length > 0 ? (
                            <table style={styles.detailTable}>
                                <thead>
                                    <tr>
                                        <th style={getDetailHeaderStyle(styles.detailTh)}>Payé par</th>
                                        <th style={getDetailHeaderStyle(styles.detailTh)}>Mode de paiement</th>
                                        <th style={getDetailHeaderStyle(styles.detailTh)}>Montant réglé</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paymentsToDisplay.map((paiement, index) => (
                                        <tr key={paiement.id || index}>
                                            <td style={getDetailCellStyle(styles.detailTd)}>
                                                {getPersonName(paiement.payer_person_id || facture.person_id)}
                                            </td>
                                            <td style={getDetailCellStyle(styles.detailTd)}>{paiement.mode || paiement.payment_mode}</td>
                                            <td style={getDetailCellStyle(styles.detailTd)}>{formatCurrency(paiement.montant || paiement.amount_paid)} €</td>
                                        </tr>
                                    ))}
                                    {/* Ligne de total */}
                                    <tr style={{ fontWeight: 'bold', backgroundColor: themeColors.navActive }}>
                                        <td colSpan="2" style={getDetailCellStyle(styles.detailTd)}>Total Payé</td>
                                        <td style={getDetailCellStyle(styles.detailTd)}>
                                            <span style={{ color: themeColors.text }}>{formatCurrency(totalPaid)} €</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        ) : (
                            <p style={{ margin: '10px 0 10px 20px', fontStyle: 'italic', color: themeColors.text }}>
                                Aucun paiement enregistré pour cette facture.
                            </p>
                        )}
                    </td>
                </tr>
            )}
        </>
    );
}